import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RISK_LEVEL_META } from "../../config/elderRiskAssessment";
import "./ClinicalScreeningOutputModal.css";

function buildPatientRef() {
  return `PAT-${Math.floor(1000 + Math.random() * 9000)}`;
}

function TrustIllustration({ riskLevel }) {
  const accent =
    riskLevel === "high" ? "#ef4444" : riskLevel === "moderate" ? "#f97316" : "#2563eb";
  const soft =
    riskLevel === "high" ? "#fecaca" : riskLevel === "moderate" ? "#fed7aa" : "#dbeafe";
  return (
    <svg className="clinical-screening__art-svg" viewBox="0 0 240 210" fill="none" aria-hidden="true">
      <ellipse cx="42" cy="158" rx="46" ry="26" fill={soft} opacity="0.55" transform="rotate(-16 42 158)" />
      <ellipse cx="198" cy="92" rx="48" ry="28" fill={soft} opacity="0.4" transform="rotate(18 198 92)" />
      <ellipse cx="152" cy="176" rx="34" ry="18" fill={soft} opacity="0.5" />
      <rect x="84" y="26" width="72" height="132" rx="13" fill="#fff" stroke="#C5D9EF" strokeWidth="1.5" />
      <rect x="91" y="40" width="58" height="100" rx="5" fill="#F7FAFE" />
      <circle cx="120" cy="72" r="13" fill="#C5D9F2" />
      <path d="M103 98c0-9 8-15 17-15s17 6 17 15" fill="#C5D9F2" />
      <path d="M120 124l-16-8V94c0-6 7-10 16-10s16 4 16 10v22l-16 8z" fill={accent} />
      <path d="M113 102l5 5 10-10" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CaregiverMatchCard({ caregiver, riskLevel, onAdd, onViewProfile, busy }) {
  const isHigh = riskLevel === "high";
  const photo = caregiver.profileImageUrl || caregiver.profileImage || "";
  const initials = (caregiver.name || "C")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const tags = caregiver.tags?.length ? caregiver.tags : caregiver.skills || [];

  return (
    <article className="clinical-screening__caregiver-card">
      <div className="clinical-screening__caregiver-top">
        <div className="clinical-screening__avatar" aria-hidden="true">
          {photo ? <img src={photo} alt="" /> : <span>{initials}</span>}
        </div>
        <div className="clinical-screening__caregiver-info">
          <h4>{caregiver.name}</h4>
          <p className="clinical-screening__caregiver-role">{caregiver.designation || "Care Attendant"}</p>
          <p className="clinical-screening__caregiver-meta">
            <span className="clinical-screening__rating">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                  fill="currentColor"
                />
              </svg>
              {caregiver.rating ? Number(caregiver.rating).toFixed(1) : "New"}
            </span>
            {caregiver.reviewCount ? ` (${caregiver.reviewCount} reviews)` : ""}
            {caregiver.years ? ` · ${caregiver.years}+ years exp.` : ""}
          </p>
        </div>
      </div>
      {tags.length ? (
        <div className="clinical-screening__tags">
          {tags.slice(0, 3).map((tag) => (
            <span key={tag} className="clinical-screening__tag">
              {tag}
            </span>
          ))}
        </div>
      ) : null}
      <div className="clinical-screening__caregiver-actions">
        {isHigh ? (
          <button
            type="button"
            className="clinical-screening__interview-btn"
            onClick={() => onAdd(caregiver)}
            disabled={busy}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            Initiate an Interview →
          </button>
        ) : (
          <button type="button" className="clinical-screening__link-btn" onClick={() => onViewProfile(caregiver)}>
            View Profile →
          </button>
        )}
      </div>
    </article>
  );
}

function CareManagerOverlay({ seniorName, customerEmail, onContinue, onContinueAnyway, busy }) {
  return (
    <div className="clinical-screening__cm-scrim" role="presentation">
      <div
        className="clinical-screening__cm-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cm-popup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="clinical-screening__cm-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
          </svg>
        </div>
        <h3 id="cm-popup-title">Care Manager Contact Initiated!</h3>
        <p>
          We have flagged <strong>{seniorName || "your loved one"}&apos;s</strong> profile for priority clinical review.
          An expert NEMA Care Manager will contact you
          {customerEmail ? (
            <>
              {" "}
              at <strong>{customerEmail}</strong>
            </>
          ) : null}{" "}
          within 2 hours.
        </p>
        <div className="clinical-screening__cm-steps">
          <strong>Next Steps:</strong>
          <ul>
            <li>
              <span className="clinical-screening__cm-step-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72" />
                </svg>
              </span>
              Comprehensive 45-minute clinical screening phone call
            </li>
            <li>
              <span className="clinical-screening__cm-step-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <path d="M9 22V12h6v10" />
                </svg>
              </span>
              Optional home safety survey coordination
            </li>
            <li>
              <span className="clinical-screening__cm-step-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </span>
              Customized high-acuity nurse matching proposal
            </li>
          </ul>
        </div>
        <button type="button" className="clinical-screening__cm-primary" onClick={onContinue} disabled={busy}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
          Proceed to booking preferred care staff/nurse →
        </button>
        <button
          type="button"
          className="clinical-screening__cm-skip"
          onClick={onContinueAnyway}
          disabled={busy}
        >
          Skip
        </button>
      </div>
    </div>
  );
}

export default function ClinicalScreeningOutputModal({
  open,
  seniorName = "",
  assessment,
  caregivers = [],
  matchCriteria = null,
  matchesLoading = false,
  customerEmail = "",
  onContinue,
  onContinueAnyway,
  onRetake,
  onAddCaregiver,
  busy = false,
}) {
  const navigate = useNavigate();
  const patientRef = useMemo(() => buildPatientRef(), [seniorName, open]);
  const [careManagerOpen, setCareManagerOpen] = useState(false);

  useEffect(() => {
    if (!open) setCareManagerOpen(false);
  }, [open, assessment?.riskLevel]);

  if (!open || !assessment) return null;

  const meta = assessment.meta || RISK_LEVEL_META[assessment.riskLevel] || RISK_LEVEL_META.low;
  const riskLevel = assessment.riskLevel || "low";
  const riskClass = `clinical-screening--${riskLevel}`;
  const isHighRisk = riskLevel === "high";

  const displayCaregivers = caregivers
    .filter((c) => c?.id && !String(c.id).startsWith("demo-"))
    .map((c) => ({
      ...c,
      tags: c.tags?.length ? c.tags : c.skills || [],
    }));

  function viewProfile(caregiver) {
    if (!caregiver?.id) return;
    navigate(`/caregivers/${caregiver.id}`);
  }

  function handlePrimaryCta() {
    if (isHighRisk) {
      setCareManagerOpen(true);
      return;
    }
    onContinue?.();
  }

  return (
    <div className="clinical-screening__backdrop" role="dialog" aria-modal="true" aria-labelledby="clinical-screening-title">
      <div className={`clinical-screening ${riskClass}`} data-risk={riskLevel}>
        <aside className="clinical-screening__sidebar">
          <div className="clinical-screening__sidebar-top">
            <div className="clinical-screening__shield-wrap" aria-hidden="true">
              <div className="clinical-screening__shield">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" strokeWidth="2.5" />
                </svg>
              </div>
              <span className="clinical-screening__shield-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
            </div>
            <h1>
              Assessment Completed <em>Successfully</em>
            </h1>
            <p>
              We have completed screening daily living parameters for <strong>{seniorName || "your loved one"}</strong>.
            </p>
          </div>

          <div className="clinical-screening__art" aria-hidden="true">
            <TrustIllustration riskLevel={riskLevel} />
          </div>

          <div className="clinical-screening__trust-card">
            <h2>Your Data is Safe with Us</h2>
            <ul>
              <li>
                <span className="clinical-screening__trust-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                </span>
                <div>
                  <strong>Secure &amp; Private</strong>
                  <span>Your information is encrypted and secure.</span>
                </div>
              </li>
              <li>
                <span className="clinical-screening__trust-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                </span>
                <div>
                  <strong>Trusted Platform</strong>
                  <span>Background verified caregivers.</span>
                </div>
              </li>
              <li>
                <span className="clinical-screening__trust-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 18v-6a9 9 0 0118 0v6" />
                    <path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z" />
                  </svg>
                </span>
                <div>
                  <strong>24/7 Support</strong>
                  <span>We&apos;re here to help you.</span>
                </div>
              </li>
            </ul>
          </div>
        </aside>

        <main className="clinical-screening__main">
          <button type="button" className="clinical-screening__close" onClick={onContinueAnyway} aria-label="Close">
            ×
          </button>

          <header className="clinical-screening__header">
            <span className="clinical-screening__status-pill">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              ASSESSMENT COMPLETED SUCCESSFULLY
            </span>
            <h2 id="clinical-screening-title">Clinical Screening Output</h2>
            <p>
              We have completed screening daily living parameters for <strong>{seniorName || "your loved one"}</strong>.
            </p>
          </header>

          <section className="clinical-screening__result-card" data-risk={riskLevel}>
            <div className="clinical-screening__result-top">
              <div className="clinical-screening__badges">
                <span className="clinical-screening__risk-badge">{meta.label}</span>
                <span className="clinical-screening__system-badge">SYSTEM RESULT</span>
              </div>
              <span className="clinical-screening__patient-ref">PATIENT REF: {patientRef}</span>
            </div>

            <h3 className="clinical-screening__result-title">
              {(riskLevel === "moderate" || riskLevel === "high") && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <path d="M12 9v4M12 17h.01" />
                </svg>
              )}
              {meta.title}
            </h3>
            <p>{meta.summary}</p>

            {riskLevel !== "low" && assessment.attentionAreas?.length ? (
              <div className="clinical-screening__attention">
                <strong>Areas that need attention</strong>
                <ul>
                  {assessment.attentionAreas.map((area) => (
                    <li key={area}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <button type="button" className="clinical-screening__primary" onClick={handlePrimaryCta} disabled={busy}>
              {meta.cta} →
            </button>
          </section>

          <section className="clinical-screening__matches">
            <div className="clinical-screening__matches-head">
              <h3>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Personalized Care matches
              </h3>
              <span className="clinical-screening__cert-badge">{meta.certifiedBadge}</span>
            </div>
            <p>Caregivers certified in matching risk parameters for {seniorName || "your loved one"}.</p>
            {matchCriteria?.patientConditions?.length ? (
              <p className="clinical-screening__match-note">
                Matched for: {matchCriteria.patientConditions.slice(0, 4).join(", ")}
                {matchCriteria.skills?.length
                  ? ` · Skills: ${matchCriteria.skills.slice(0, 3).join(", ")}`
                  : ""}
              </p>
            ) : null}
            <div className="clinical-screening__caregiver-list">
              {matchesLoading ? (
                <p className="clinical-screening__matches-loading">Finding caregivers matched to this condition…</p>
              ) : displayCaregivers.length ? (
                displayCaregivers.slice(0, 2).map((c) => (
                  <CaregiverMatchCard
                    key={c.id}
                    caregiver={c}
                    riskLevel={riskLevel}
                    onAdd={onAddCaregiver}
                    onViewProfile={viewProfile}
                    busy={busy}
                  />
                ))
              ) : (
                <div className="clinical-screening__matches-empty">
                  <p>No verified caregivers matched this profile in your area yet.</p>
                  <p>Continue to browse all caregivers or our care team will help with manual matching.</p>
                </div>
              )}
            </div>
          </section>

          <footer className="clinical-screening__footer">
            <div className="clinical-screening__footer-bar">
              <div className="clinical-screening__disclaimer">
                <h4>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" strokeWidth="2.5" />
                  </svg>
                  Important Business Rule &amp; Disclaimer
                </h4>
                <div className="clinical-screening__disclaimer-copy">
                  <p>
                    This assessment is only a screening tool. It does not constitute a medical diagnosis.
                  </p>
                  <p>
                    The final risk category may be reviewed and updated manually by the NEMA Care Operations Team after
                    reviewing the patient&apos;s clinical information.
                  </p>
                  <p>
                    The purpose of this assessment is to recommend the most suitable caregiver and identify customers who
                    may benefit from hiring a Care Manager.
                  </p>
                </div>
              </div>
              <div className="clinical-screening__footer-actions">
                <button type="button" className="clinical-screening__secondary" onClick={onRetake} disabled={busy}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M23 4v6h-6M1 20v-6h6" />
                    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                  </svg>
                  Retake Assessment
                </button>
                <button
                  type="button"
                  className="clinical-screening__skip-link"
                  onClick={onContinueAnyway}
                  disabled={busy}
                >
                  Skip
                </button>
              </div>
            </div>
          </footer>
        </main>

        {careManagerOpen ? (
          <CareManagerOverlay
            seniorName={seniorName}
            customerEmail={customerEmail}
            onContinue={onContinue}
            onContinueAnyway={onContinueAnyway}
            busy={busy}
          />
        ) : null}
      </div>
    </div>
  );
}

